import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "./ui/button";
import { Loader2, FileText, Calendar, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_venture-mentor-2/artifacts/52nzs5st_ChatGPT%20Image%20Jan%2022%2C%202026%2C%2002_22_56%20PM.png";

const MySessions = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
      loadSessions();
    } catch (error) {
      toast.error("Please log in to view your sessions");
      navigate("/");
    }
  };

  const loadSessions = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/sessions/my-sessions`, { withCredentials: true });
      setSessions(response.data);
    } catch (error) {
      toast.error("Failed to load sessions");
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      toast.success("Logged out successfully");
      navigate("/");
    } catch (error) {
      toast.error("Logout failed");
    }
  };

  const getPhaseDisplay = (phase) => {
    if (phase === "questioning") return "In Progress";
    if (phase === "ready_for_evaluation" || phase === "evaluating") return "Ready to Evaluate";
    return "Evaluated";
  };

  const handleContinueSession = (session) => {
    if (session.phase === "questioning" || session.phase === "ready_for_evaluation") {
      navigate("/validate", { state: { sessionId: session.session_id } });
    } else {
      navigate(`/results/${session.session_id}`);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="border-b border-[#E2E8F0] px-6 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div 
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => navigate("/")}
            data-testid="header-title"
          >
            <img src={LOGO_URL} alt="9Mantra Logo" className="h-10 w-auto" />
            <h1 className="text-2xl font-normal text-[#1A1A1A]">9Mantra</h1>
          </div>
          <div className="flex items-center gap-4">
            {user && (
              <div className="flex items-center gap-3">
                {user.picture && (
                  <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
                )}
                <span className="text-sm text-[#64748B]">{user.name}</span>
              </div>
            )}
            <Button onClick={handleLogout} variant="outline" className="border-[#E2E8F0]">
              Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-3xl text-[#1A1A1A]" data-testid="my-sessions-heading">
            My Validation Sessions
          </h2>
          <Button
            onClick={() => navigate("/validate")}
            className="bg-[#0047AB] hover:bg-[#003580] text-white"
            data-testid="new-validation-btn"
          >
            New Validation
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-[#0047AB]" />
          </div>
        ) : sessions.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="h-16 w-16 text-[#E2E8F0] mx-auto mb-4" />
            <p className="text-[#64748B] mb-6">No validation sessions yet</p>
            <Button
              onClick={() => navigate("/validate")}
              className="bg-[#0047AB] hover:bg-[#003580] text-white"
            >
              Start Your First Validation
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {sessions.map((session, idx) => (
              <div
                key={idx}
                className="bg-white border border-[#E2E8F0] p-6 hover:-translate-y-1 transition-all duration-300 cursor-pointer"
                onClick={() => handleContinueSession(session)}
                data-testid={`session-${idx}`}
              >
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <p className="text-base text-[#1A1A1A] line-clamp-2 mb-2">
                      {session.business_idea.substring(0, 150)}...
                    </p>
                    <div className="flex items-center gap-4 text-sm text-[#64748B]">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        {new Date(session.created_at).toLocaleDateString()}
                      </span>
                      <span className="font-medium">
                        {getPhaseDisplay(session.phase)}
                      </span>
                      {session.phase === "questioning" && (
                        <span>Question {session.questions_asked} of 10</span>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    className="border-[#E2E8F0] text-[#0047AB]"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleContinueSession(session);
                    }}
                  >
                    {session.phase === "questioning" ? "Continue" : "View Results"}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MySessions;